import {
    type FillMultipleShiftsResponse,
    type ShiftFillError,
    type ShiftFillFailureGroup,
    type WellSkyAssignmentReviewRead,
} from '@libraries/typescript/api_client';

export type FillResultState = {
    readonly failureGroups: ReadonlyArray<ShiftFillFailureGroup>;
    readonly succeeded: ReadonlySet<string>;
};

export type WellSkyShiftFailure = {
    readonly shiftId: string;
    readonly wellSkyErrors: WellSkyAssignmentReviewRead;
};

const RETRY_STRATEGY_PRIORITY: ReadonlyArray<
    ShiftFillFailureGroup['retry_strategy']
> = [
    'manual_reconciliation',
    'retry_with_overrides',
    'select_rate',
    'accept_overtime',
    'select_poc',
    'retry_with_ehr',
    'assign_without_ehr',
];

function getRetryStrategy(
    failure: ShiftFillError,
): ShiftFillFailureGroup['retry_strategy'] {
    switch (failure.error_type) {
        case 'generations_manual_reconciliation':
            return 'manual_reconciliation';
        case 'wellsky_recoverable':
            return 'retry_with_overrides';
        case 'wellsky_rate_selection':
            return 'select_rate';
        case 'hhaexchange_overtime':
            return 'accept_overtime';
        case 'hhaexchange_ambiguous_poc':
            return 'select_poc';
        case 'hhaexchange_portal_retryable':
        case 'hhaexchange_stale_visit':
        case 'shift_not_fillable':
        case 'validation_error':
        case 'wellsky_stale_shift':
            return 'retry_with_ehr';
        default:
            return 'assign_without_ehr';
    }
}

function getResponseFailureGroups(
    response: FillMultipleShiftsResponse,
): ReadonlyArray<ShiftFillFailureGroup> {
    if (response.failure_groups) {
        return response.failure_groups;
    }

    // Older cached clients and test fixtures may still return only the
    // compatibility fields. New responses always include failure_groups.
    const failures = response.failed_shifts?.length
        ? response.failed_shifts
        : response.failed_shift
          ? [response.failed_shift]
          : [];
    return failures.map((failure, index) => ({
        failure,
        not_attempted_shift_ids:
            index === 0 ? response.not_attempted_shift_ids : [],
        retry_strategy: getRetryStrategy(failure),
    }));
}

export function getFailureGroupShiftIds(
    group: ShiftFillFailureGroup,
): ReadonlyArray<string> {
    return [group.failure.shift_id, ...(group.not_attempted_shift_ids ?? [])];
}

export function mergeFillAttempt(
    previous: FillResultState | null,
    attemptedShiftIds: ReadonlySet<string>,
    response: FillMultipleShiftsResponse,
    shiftOrder: ReadonlyMap<string, number>,
): FillResultState {
    const succeeded = new Set(previous?.succeeded ?? []);
    for (const shiftId of response.succeeded_shift_ids) {
        succeeded.add(shiftId);
    }

    const preservedGroups =
        previous?.failureGroups.filter((group) =>
            getFailureGroupShiftIds(group).every(
                (shiftId) => !attemptedShiftIds.has(shiftId),
            ),
        ) ?? [];
    const failureGroups = [
        ...preservedGroups,
        ...getResponseFailureGroups(response),
    ].sort(
        (left, right) =>
            (shiftOrder.get(left.failure.shift_id) ?? Number.MAX_SAFE_INTEGER) -
            (shiftOrder.get(right.failure.shift_id) ?? Number.MAX_SAFE_INTEGER),
    );

    return { failureGroups, succeeded };
}

export function getActiveRetryGroups(
    state: FillResultState,
): ReadonlyArray<ShiftFillFailureGroup> {
    const activeStrategy = RETRY_STRATEGY_PRIORITY.find((strategy) =>
        state.failureGroups.some((group) => group.retry_strategy === strategy),
    );
    if (!activeStrategy) {
        return [];
    }
    return state.failureGroups.filter(
        (group) => group.retry_strategy === activeStrategy,
    );
}

export function getRetryShiftIds(
    groups: ReadonlyArray<ShiftFillFailureGroup>,
): ReadonlySet<string> {
    return new Set(groups.flatMap(getFailureGroupShiftIds));
}

export function getFailuresByShiftId(
    state: FillResultState,
): ReadonlyMap<string, ShiftFillError> {
    return new Map(
        state.failureGroups.map((group) => [
            group.failure.shift_id,
            group.failure,
        ]),
    );
}

export function getNotAttemptedShiftIds(
    state: FillResultState,
): ReadonlySet<string> {
    return new Set(
        state.failureGroups.flatMap(
            (group) => group.not_attempted_shift_ids ?? [],
        ),
    );
}

export function getWellSkyShiftFailures(
    groups: ReadonlyArray<ShiftFillFailureGroup>,
): ReadonlyArray<WellSkyShiftFailure> {
    return groups.flatMap((group) => {
        const { failure } = group;
        if (
            (failure.error_type !== 'wellsky_recoverable' &&
                failure.error_type !== 'wellsky_unrecoverable') ||
            !failure.wellsky_errors
        ) {
            return [];
        }
        return [
            {
                shiftId: failure.shift_id,
                wellSkyErrors: failure.wellsky_errors,
            },
        ];
    });
}
